// src/pages/Register.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, signInWithGoogle } from "../utils/firebase";
import { FaEye, FaEyeSlash, FaGoogle, FaUser, FaPhone, FaEnvelope, FaLock, FaCheck } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/Register.css";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  // Auto-focus name input on mount
  useEffect(() => {
    const nameInput = document.querySelector('input[name="name"]');
    if (nameInput) nameInput.focus();
  }, []);

  // Check password strength
const { password } = formData;

// Check password strength
  useEffect(() => {
    let strength = 0;

    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    setPasswordStrength(strength);
  }, [password]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[6-9]\d{9}$/;

    if (!formData.name.trim()) {
      newErrors.name = "Full name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!mobileRegex.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid Indian mobile number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    } else if (passwordStrength < 2) {
      newErrors.password = "Please use a stronger password";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, passwordStrength]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const getPasswordStrengthLabel = useCallback(() => {
    const labels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
    return labels[passwordStrength] || "Very Weak";
  }, [passwordStrength]);

  const getPasswordStrengthColor = useCallback(() => {
    const colors = ["#f44336", "#ff9800", "#ffc107", "#4caf50", "#2e7d32"];
    return colors[passwordStrength] || "#f44336";
  }, [passwordStrength]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      
      await updateProfile(user, { 
        displayName: formData.name 
      });

      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email.toLowerCase(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        emailVerified: false,
      });

      toast.success("🎉 Account created successfully!", {
        icon: "✅",
        theme: "colored",
        autoClose: 2000,
      });
      
      setTimeout(() => navigate("/"), 1500);
    } catch (error) {
      console.error("Registration error:", error);
      let errorMessage = "Registration failed. Please try again.";
      
      switch (error.code) {
        case "auth/email-already-in-use":
          errorMessage = "Email already registered. Try logging in.";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address";
          break;
        case "auth/operation-not-allowed":
          errorMessage = "Email/password accounts are not enabled";
          break;
        case "auth/weak-password":
          errorMessage = "Password is too weak";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your connection";
          break;
        default:
          errorMessage = "Registration failed. Please try again";
      }
      
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      const user = result.user;

      // Store user data in Firestore
      await setDoc(
        doc(db, "users", user.uid),
        {
          uid: user.uid,
          name: user.displayName || "",
          email: user.email,
          mobile: "",
          photoURL: user.photoURL || "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          emailVerified: user.emailVerified,
          provider: "google",
        },
        { merge: true }
      );

      toast.success("✅ Signed up with Google successfully!", {
        icon: "👏",
        theme: "colored",
      });
      
      setTimeout(() => navigate("/"), 1200);
    } catch (error) {
      console.error("Google registration error:", error);
      let errorMessage = "Google sign-up failed";
      
      switch (error.code) {
        case "auth/popup-closed-by-user":
          errorMessage = "Sign-up cancelled";
          break;
        case "auth/popup-blocked":
          errorMessage = "Popup blocked. Please allow popups for this site";
          break;
        case "auth/cancelled-popup-request":
          errorMessage = "Sign-up process cancelled";
          break;
        case "auth/unauthorized-domain":
          errorMessage = "This domain is not authorized for Google sign-in";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your connection";
          break;
        default:
          errorMessage = "Google sign-up failed. Please try again";
      }
      
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading) {
      handleSubmit(e);
    }
  };

  return (
    <div className="register-page" onKeyPress={handleKeyPress}>
      <ToastContainer 
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="register-card">
        <div className="register-header">
          <h2>Create Your Account</h2>
          <p className="register-subtitle">Join Sri Ayyappa Catering Service</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form" noValidate>
          <div className="input-group">
            <label htmlFor="name">
              <FaUser className="input-icon" /> Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              autoComplete="name"
              disabled={loading}
              className={errors.name ? "error" : ""}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="input-row">
            <div className="input-group">
              <label htmlFor="mobile">
                <FaPhone className="input-icon" /> Mobile Number
              </label>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                autoComplete="tel"
                maxLength="10"
                disabled={loading}
                className={errors.mobile ? "error" : ""}
              />
              {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            </div>

            <div className="input-group">
              <label htmlFor="email">
                <FaEnvelope className="input-icon" /> Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                autoComplete="email"
                disabled={loading}
                className={errors.email ? "error" : ""}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label htmlFor="password">
                <FaLock className="input-icon" /> Password
              </label>
              <div className="password-wrapper">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  disabled={loading}
                  className={errors.password ? "error" : ""}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((p) => !p)}
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex="0"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {formData.password && (
                <div className="password-strength">
                  <div className="strength-meter">
                    <div 
                      className="strength-bar"
                      style={{
                        width: `${passwordStrength * 25}%`,
                        backgroundColor: getPasswordStrengthColor()
                      }}
                    ></div>
                  </div>
                  <span className="strength-label">
                    Strength: <strong style={{ color: getPasswordStrengthColor() }}>
                      {getPasswordStrengthLabel()}
                    </strong>
                  </span>
                </div>
              )}
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword">
                <FaLock className="input-icon" /> Confirm Password
              </label>
              <div className="password-wrapper">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  disabled={loading}
                  className={errors.confirmPassword ? "error" : ""}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword((p) => !p)}
                  disabled={loading}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  tabIndex="0"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {formData.password && formData.confirmPassword && (
                <div className="password-match">
                  {formData.password === formData.confirmPassword ? (
                    <span className="match-success">
                      <FaCheck /> Passwords match
                    </span>
                  ) : (
                    <span className="match-error">
                      ❌ Passwords do not match
                    </span>
                  )}
                </div>
              )}
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>
          </div>

          <div className="terms-agreement">
            <input 
              type="checkbox" 
              id="terms" 
              name="terms" 
              required 
              disabled={loading}
            />
            <label htmlFor="terms">
              I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
            </label>
          </div>

          <button
            type="submit"
            className="register-btn"
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? (
              <span className="loading-spinner"></span>
            ) : (
              "Create Account"
            )}
          </button>
        </form>

        <div className="divider">
          <span>or sign up with</span>
        </div>

        <button
          className="google-btn"
          onClick={handleGoogleRegister}
          disabled={loading}
          aria-label="Sign up with Google"
        >
          <FaGoogle className="google-icon" />
          Sign up with Google
        </button>

        <div className="login-link">
          <p>
            Already have an account?{" "}
            <Link to="/login" className="login-cta">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;